const STOP_WORDS = new Set([
    'a', 'an', 'the', 'is', 'am', 'are', 'was', 'were', 'at', 'which', 'on', 'for', 'to', 'of', 'in', 'and', 'or', 'with',
    'about', 'how', 'what', 'who', 'where', 'why', 'do', 'does', 'did', 'can', 'could', 'i', 'you', 'he', 'she', 'it', 'we', 'they',
    'me', 'him', 'her', 'us', 'them', 'my', 'your', 'his', 'our', 'their', 'tell', 'that', 'this', 'these', 'those', 'be', 'been', 'being',
    'please', 'actually', 'now', 'today', 'available', 'current', 'many', 'much', 'costly',
    // Hindi stop words
    'का', 'की', 'के', 'में', 'पर', 'से', 'को', 'और', 'या', 'है', 'हैं', 'था', 'थी', 'थे', 'क्या', 'कौन', 'कहाँ', 'कैसे', 'आप', 'आपका', 'आपकी', 'अपने', 'मुझे', 'बताएं', 'बताओ', 'जी'
]);

const SYNONYMS: Record<string, string[]> = {
    'hello': ['hi', 'hii', 'hey', 'helo', 'hola', 'greetings', 'welcome', 'morning', 'afternoon', 'evening', 'नमस्ते', 'नमस्कार', 'हेलो', 'प्रणाम'],
    'ceo': ['boss', 'founder', 'owner', 'head', 'chief', 'director', 'leader', 'harshada', 'mahajan', 'सीईओ', 'मालिक', 'संस्थापक'],
    'manager': ['lead', 'supervisor', 'sagar', 'mali', 'मैनेजर', 'प्रबंधक'],
    'hr': ['human', 'resources', 'recruitment', 'hiring', 'akshita', 'bagul', 'एचआर'],
    'cost': ['price', 'pricing', 'fee', 'charge', 'rate', 'money', 'budget', 'expensive', 'cheap', 'valuation', 'कीमत', 'मूल्य', 'शुल्क', 'प्राइस', 'खर्च'],
    'employees': ['staff', 'team', 'size', 'people', 'workers', 'members', 'professionals', 'devs', 'developers', 'कर्मचारी', 'टीम', 'सदस्य', 'लोग'],
    'services': ['offerings', 'work', 'solutions', 'provide', 'development', 'apps', 'web', 'mobile', 'ai', 'cloud', 'सेवाएं', 'सर्विस', 'काम', 'सुविधाएं'],
    'startups': ['small', 'new', 'mvp', 'entrepreneur', 'स्टार्टअप'],
    'devnectar': ['company', 'organization', 'firm', 'agency', 'business', 'office', 'place', 'कंपनी'],
    'location': ['address', 'pune', 'where', 'place', 'visit', 'office', 'पता', 'ऑफिस', 'स्थान', 'जगह', 'पुणे'],
    'support': ['maintenance', 'help', 'ongoing', 'assist', 'assistance', 'support', 'fix', 'सहायता', 'मदद', 'सपोर्ट'],
    'process': ['methodology', 'flow', 'steps', 'workflow', 'agile', 'scrum', 'प्रक्रिया'],
    'quality': ['testing', 'QA', 'bugs', 'secure', 'security', 'code', 'review', 'गुणवत्ता']
};

const mapSynonym = (word: string): string => {
    for (const [key, values] of Object.entries(SYNONYMS)) {
        if (values.includes(word) || key === word) {
            return key;
        }
    }
    return word;
};

/**
 * Tokenizes a string into an array of lowercase words,
 * removing stop words and resolving synonyms (supporting English & Devanagari Hindi).
 */
export const tokenize = (str: string): string[] => {
    const rawTokens = str.toLowerCase().replace(/[^\w\s\u0900-\u097F]/g, '').split(/\s+/).filter(Boolean);
    return rawTokens
        .filter(w => !STOP_WORDS.has(w))
        .map(mapSynonym);
};

/**
 * Calculates the Levenshtein distance between two strings.
 * Used for spelling tolerance / fuzzy matching.
 */
export const levenshteinDistance = (a: string, b: string): number => {
    const bLen = b.length;
    const aLen = a.length;
    const matrix: number[][] = Array.from({ length: bLen + 1 }, () => Array(aLen + 1).fill(0));

    for (let i = 0; i <= bLen; i++) {
        const row = matrix[i];
        if (row) row[0] = i;
    }
    const firstRow = matrix[0];
    if (firstRow) {
        for (let j = 0; j <= aLen; j++) {
            firstRow[j] = j;
        }
    }

    for (let i = 1; i <= bLen; i++) {
        for (let j = 1; j <= aLen; j++) {
            const row = matrix[i];
            const prevRow = matrix[i - 1];
            if (!row || !prevRow) continue;

            if (b.charAt(i - 1) === a.charAt(j - 1)) {
                row[j] = prevRow[j - 1] ?? 0;
            } else {
                row[j] = Math.min(
                    (prevRow[j - 1] ?? 0) + 1, // substitution
                    (row[j - 1] ?? 0) + 1,     // insertion
                    (prevRow[j] ?? 0) + 1      // deletion
                );
            }
        }
    }

    const lastRow = matrix[bLen];
    return lastRow ? (lastRow[aLen] ?? 0) : 0;
};

/**
 * Calculates a similarity score between 0 and 1.
 * 1 means exact match, 0 means completely different.
 */
export const similarity = (s1: string, s2: string): number => {
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;

    if (longer.length === 0) return 1.0;

    const distance = levenshteinDistance(longer.toLowerCase(), shorter.toLowerCase());
    return (longer.length - distance) / longer.length;
};

interface FAQItem {
    question: string;
    answer: string;
    redirect?: string;
}

/**
 * Finds the best matching FAQ for a given user query.
 */
export const findBestMatch = (query: string, faqs: FAQItem[]): FAQItem | null => {
    const queryTokens = tokenize(query);
    if (queryTokens.length === 0) return null;

    let bestMatch: FAQItem | null = null;
    let highestScore = 0;

    // Threshold for matching
    const threshold = 0.6;

    for (const faq of faqs) {
        const faqTokens = tokenize(faq.question);

        // 1. Check for whole-phrase similarity
        const phraseScore = similarity(query, faq.question);

        // 2. Check for individual token overlap (keyword matching)
        let tokenOverlap = 0;
        for (const qToken of queryTokens) {
            for (const fToken of faqTokens) {
                if (similarity(qToken, fToken) > 0.8) {
                    tokenOverlap++;
                    break;
                }
            }
        }
        const tokenScore = (tokenOverlap / Math.max(queryTokens.length, faqTokens.length));

        // Combined score
        const combinedScore = (phraseScore * 0.4) + (tokenScore * 0.6);

        if (combinedScore > highestScore && combinedScore >= threshold) {
            highestScore = combinedScore;
            bestMatch = faq;
        }
    }

    return bestMatch;
};
